/**
 * User EEOC Mock Test Page
 * Practice test that doesn't count toward score
 * 
 * Features:
 * - Two-way verbal communication
 * - Speech-to-Text using LemonFox (backend)
 * - Text-to-Speech using Web Speech API (free, browser-based)
 * - Fallback text input for users without microphone
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Shield,
  Play,
  Send,
  Loader2,
  ArrowLeft,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  User,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Bot,
  UserCircle,
  Keyboard,
  StopCircle,
  PlayCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Web Speech API TTS helper
const speak = (text, onEnd, rate = 1.0) => {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) {
      console.warn('Web Speech API not supported');
      if (onEnd) onEnd();
      resolve();
      return;
    }
    
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = rate;
    utterance.pitch = 1.0;
    utterance.volume = 1;
    
    // Try to use a natural female voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Samantha') || 
      v.name.includes('Google US English Female') ||
      v.name.includes('Microsoft Zira') ||
      (v.lang.startsWith('en') && v.name.toLowerCase().includes('female'))
    ) || voices.find(v => v.lang.startsWith('en')) || voices[0];
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onend = () => {
      if (onEnd) onEnd();
      resolve();
    };
    utterance.onerror = () => {
      if (onEnd) onEnd();
      resolve();
    };
    
    window.speechSynthesis.speak(utterance);
  });
};

// Stop speaking
const stopSpeaking = () => {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel();
  }
};

export default function UserEEOCMockTest() {
  const { userUrl } = useParams();
  const navigate = useNavigate();
  const { user: authUser, tenant } = useAuth();
  const context = useOutletContext() || {};
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [testStarted, setTestStarted] = useState(false);
  const [testComplete, setTestComplete] = useState(false);
  const [currentScenario, setCurrentScenario] = useState(0);
  const [totalScenarios, setTotalScenarios] = useState(2);
  const [candidateMessage, setCandidateMessage] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [linkId, setLinkId] = useState(null);
  
  // Voice state
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [textInputMode, setTextInputMode] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);
  
  // Load voices when component mounts
  useEffect(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
    
    return () => {
      stopSpeaking();
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [userUrl, authUser]);

  const fetchUserData = async () => {
    try {
      // If logged in user accessing from dashboard (no userUrl), use auth context
      if (!userUrl && authUser) {
        setUser({
          id: authUser.id,
          name: authUser.name,
          email: authUser.email,
          tenant_id: tenant?.id || authUser.tenant_id
        });
        setLoading(false);
        return;
      }
      
      // For passwordless users with userUrl
      if (userUrl) {
        const userRes = await api.get(`/users/by-url/${userUrl}`);
        setUser(userRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  // Speak a message using Web Speech API
  const speakMessage = useCallback(async (text) => {
    if (!voiceEnabled) return;
    
    setIsSpeaking(true);
    await speak(text, () => setIsSpeaking(false));
  }, [voiceEnabled]);

  const handleStartTest = async () => {
    setLoading(true);
    try {
      // Create a test session for this user - use user's tenant_id
      const tenantId = user.tenant_id || 'default';
      const createRes = await api.post(`/eeoc/admin/tests?tenant_id=${tenantId}`, {
        tenant_user_id: user.id,
        user_name: user.name,
        user_email: user.email
      });
      
      const newLinkId = createRes.data.link_id;
      setLinkId(newLinkId);
      
      // Start the mock test
      const startRes = await api.post('/eeoc/test/start', {
        link_id: newLinkId,
        is_mock: true
      });
      
      const greeting = startRes.data.candidate_greeting;
      setCandidateMessage(greeting);
      setTotalScenarios(startRes.data.scenarios?.length || 2);
      setConversationHistory([
        { role: 'candidate', content: greeting }
      ]);
      setTestStarted(true);
      
      // Speak the greeting
      await speakMessage(greeting);
      
    } catch (error) {
      console.error('Failed to start test:', error);
      toast.error('Failed to start mock test');
    } finally {
      setLoading(false);
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAndSend(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Recording... Speak now', { duration: 2000 });
    } catch (err) {
      toast.error('Microphone access denied. Switching to text mode.');
      setTextInputMode(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndSend = async (audioBlob) => {
    setIsProcessing(true);
    try {
      // Transcribe audio using backend (LemonFox)
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      
      const transcribeRes = await api.post('/eeoc/audio/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const transcribedText = transcribeRes.data.text;
      
      if (!transcribedText || transcribedText.trim().length < 3) {
        toast.error('Could not understand. Please try again or use text input.');
        setIsProcessing(false);
        return;
      }
      
      await sendMessage(transcribedText);
      
    } catch (err) {
      console.error('Transcription error:', err);
      toast.error('Failed to process audio. Switching to text mode.');
      setTextInputMode(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Send message (from voice or text)
  const sendMessage = async (messageText) => {
    if (!messageText.trim() || sending) return;
    
    setSending(true);
    const newHistory = [...conversationHistory, { role: 'user', content: messageText }];
    setConversationHistory(newHistory);
    setUserMessage('');
    
    try {
      const response = await api.post('/eeoc/chat', {
        link_id: linkId,
        message: messageText,
        is_mock: true,
        conversation_history: newHistory.map(m => ({ 
          role: m.role === 'user' ? 'user' : 'assistant', 
          content: m.content 
        }))
      });
      
      const responseText = response.data.response;
      setCandidateMessage(responseText);
      setConversationHistory([...newHistory, { role: 'candidate', content: responseText }]);
      
      // Speak the response
      await speakMessage(responseText);
      
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to get response');
    } finally {
      setSending(false);
    }
  };

  const handleSendMessage = async () => {
    if (!userMessage.trim() || sending) return;
    await sendMessage(userMessage);
  };

  const handleEndTest = () => {
    setTestComplete(true);
    toast.success('Mock test completed! Review the conversation to learn from the scenarios.');
  };

  const handleBackToEEOC = () => {
    // For logged-in users (no userUrl), go to dashboard EEOC
    if (!userUrl) {
      navigate('/dashboard/eeoc');
    } else {
      // For passwordless users
      navigate(`/user/${userUrl}/eeoc`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // Pre-test screen
  if (!testStarted) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="eeoc-mock-test">
        <Button variant="ghost" onClick={handleBackToEEOC} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to EEOC Training
        </Button>

        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Mic className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle>Verbal Mock Test</CardTitle>
                <CardDescription>Practice EEOC-compliant interviewing with voice</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            {/* How It Works */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-3">How It Works:</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-emerald-600">1</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">AI Candidate Speaks First</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">The candidate will greet you and introduce themselves verbally</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-blue-600">2</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">You Respond by Speaking</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Click the microphone and speak your interview questions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-purple-600">3</span>
                  </div>
                  <div>
                    <span className="text-sm text-slate-700 dark:text-slate-300 font-medium">Two-Way Conversation</span>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Continue the verbal dialogue - it's like a real interview!</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">What to expect:</h4>
              <ul className="text-sm text-blue-600 dark:text-blue-300 space-y-2">
                <li className="flex items-start gap-2">
                  <Volume2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>The candidate will speak using your browser&apos;s voice</span>
                </li>
                <li className="flex items-start gap-2">
                  <Mic className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Microphone required (text fallback available)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>Practice redirecting protected information disclosures</span>
                </li>
                <li className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" />
                  <span>This is practice only - no score is recorded</span>
                </li>
              </ul>
            </div>

            <Button className="w-full" size="lg" onClick={handleStartTest} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Start Verbal Practice
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Test complete screen
  if (testComplete) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card className="border-2 border-green-200">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <CardTitle>Mock Test Complete!</CardTitle>
                <CardDescription>Great practice session</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <p className="text-muted-foreground">
              You&apos;ve completed the mock test. Review the conversation below to see how you handled the EEOC scenarios.
              Remember, this was just practice - your score wasn&apos;t recorded.
            </p>
            
            <div className="p-4 bg-muted rounded-lg max-h-64 overflow-y-auto space-y-3">
              {conversationHistory.map((msg, idx) => (
                <div key={idx} className={cn("flex gap-2", msg.role === 'user' ? 'justify-end' : '')}>
                  <div className={cn(
                    "rounded-lg p-3 max-w-[80%]",
                    msg.role === 'user' 
                      ? 'bg-purple-100 dark:bg-purple-900/30' 
                      : 'bg-white dark:bg-slate-800 border'
                  )}>
                    <p className="text-xs font-medium mb-1 text-muted-foreground">
                      {msg.role === 'user' ? 'You (Recruiter)' : 'Candidate'}
                    </p>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleBackToEEOC} className="flex-1">
                Back to Training
              </Button>
              <Button onClick={() => navigate(`/user/${userUrl}/eeoc/assessment`)} className="flex-1 bg-green-600 hover:bg-green-700">
                Take Real Assessment
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Active test screen
  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="eeoc-mock-active">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="gap-1">
            <Mic className="h-3 w-3" />
            Verbal Practice
          </Badge>
          <span className="text-sm text-muted-foreground">Practice Mode - No Score</span>
        </div>
        <Button variant="outline" size="sm" onClick={handleEndTest} disabled={isSpeaking}>
          End Practice
        </Button>
      </div>

      {/* Chat Area */}
      <Card className="h-[450px] flex flex-col">
        <CardHeader className="pb-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-purple-600" />
              <span className="font-medium">Verbal Interview Simulation</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={cn(!voiceEnabled && 'text-muted-foreground')}
                data-testid="voice-enabled-toggle"
              >
                {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              <span className="text-xs text-muted-foreground">
                {voiceEnabled ? 'Audio On' : 'Audio Off'}
              </span>
              {isSpeaking && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    stopSpeaking();
                    setIsSpeaking(false);
                  }}
                  className="text-red-600 ml-2"
                >
                  <StopCircle className="h-3 w-3 mr-1" />
                  Stop
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden p-0">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-4">
              {conversationHistory.map((msg, idx) => (
                <div key={idx} className={cn("flex gap-3", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                  {msg.role !== 'user' && (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                  )}
                  <div className={cn(
                    "rounded-lg p-3 max-w-[75%]",
                    msg.role === 'user' 
                      ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' 
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                  )}>
                    <p className="text-xs font-medium mb-1 opacity-70">
                      {msg.role === 'user' ? 'You (Recruiter)' : 'Candidate'}
                    </p>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                      <UserCircle className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    </div>
                  )}
                </div>
              ))}
              {(sending || isProcessing || isSpeaking) && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="bg-slate-100 dark:bg-slate-800 rounded-lg px-4 py-2">
                    <div className="flex items-center gap-2">
                      {sending || isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm text-slate-500">{isProcessing ? 'Processing...' : 'Thinking...'}</span>
                        </>
                      ) : (
                        <>
                          <Volume2 className="w-4 h-4 animate-pulse text-emerald-600" />
                          <span className="text-sm text-slate-500">Speaking...</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
        </CardContent>
        
        {/* Input Area */}
        <div className="p-4 border-t bg-slate-50 dark:bg-slate-900/50">
          {/* Mode Toggle */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="voice-text-mode"
                checked={!textInputMode}
                onCheckedChange={(checked) => setTextInputMode(!checked)}
              />
              <Label htmlFor="voice-text-mode" className="text-sm">
                {textInputMode ? 'Text Mode' : 'Voice Mode'}
              </Label>
            </div>
          </div>
          
          {textInputMode ? (
            /* Text Input Mode */
            <div className="flex gap-2">
              <Textarea
                placeholder="Type your response as the recruiter..."
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                disabled={sending || isProcessing || isSpeaking}
                className="min-h-[60px] flex-1"
              />
              <Button 
                onClick={handleSendMessage} 
                disabled={!userMessage.trim() || sending || isProcessing || isSpeaking}
                className="self-end"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            /* Voice Input Mode */
            <div className="flex flex-col items-center">
              <Button
                onClick={isRecording ? stopRecording : startRecording}
                disabled={sending || isProcessing || isSpeaking}
                variant={isRecording ? 'destructive' : 'default'}
                size="lg"
                className={cn(
                  "h-16 w-16 rounded-full transition-all",
                  isRecording && "animate-pulse scale-110 shadow-lg shadow-red-500/30"
                )}
                data-testid="record-btn"
              >
                {isRecording ? (
                  <MicOff className="h-6 w-6" />
                ) : (
                  <Mic className="h-6 w-6" />
                )}
              </Button>
              <p className="text-sm text-muted-foreground mt-3 text-center">
                {isRecording 
                  ? '🔴 Recording... Click again to stop and send'
                  : sending || isProcessing
                    ? '⏳ Processing...'
                    : isSpeaking
                      ? '🔊 Candidate is speaking...'
                      : '🎤 Click to speak your response'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Having trouble? <button 
                  type="button"
                  onClick={() => setTextInputMode(true)} 
                  className="text-blue-600 hover:underline"
                >
                  Switch to text mode
                </button>
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
